import * as React from "react";
import { MarketplaceReleaseInstance, LegacyReleaseState, LegacyReleaseStateDeployment } from "../../types/release";
import { Table } from "../../../../newcomponents/table/table";
import { Button, Modal, notification } from "antd";
import { DateString } from "../../../../components/dateString";
import { request } from "../../../../services/cpln";
import { formatStrings } from "../../../../utils/time";
import { getMarketplaceErrorMessage } from "../../utils";
import { useAppDispatch } from "../../../../store/hooks";
import { addNotification } from "../../../../store/notificationDrawerSlice";

interface ReleaseStateDeploymentTableItem extends LegacyReleaseStateDeployment {
  isLoadingRollback: boolean;
  isLoadingRollbackThis: boolean;
}

interface Props {
  release: MarketplaceReleaseInstance;
  state: LegacyReleaseState;
  onRefresh?: () => Promise<void>;
}
export const LegacyReleaseRevisions: React.FC<Props> = ({ release, state, onRefresh }) => {
  const dispatch = useAppDispatch();
  const [isLoadingRollbackRevision, setIsLoadingRollbackRevision] = React.useState<number | null>(null);
  const [isLoadingRollback, setIsLoadingRollback] = React.useState(false);

  async function handleRollback(revision: number) {
    const gvcDisplay = release.gvc && release.gvc !== " " ? release.gvc : null;
    Modal.confirm({
      title: "Confirm Rollback",
      content: (
        <div className="flex flex-col gap-2">
          <div>Are you sure you want to rollback this release to revision {revision}?</div>
          {gvcDisplay && (
            <div>
              <span className="font-medium">GVC:</span>{" "}
              <span className="font-bold" style={{ color: "var(--color-primary)" }}>
                {gvcDisplay}
              </span>
            </div>
          )}
          <div>
            <span className="font-medium">Release Name:</span>{" "}
            <span className="font-bold" style={{ color: "var(--color-primary)" }}>
              {release.name}
            </span>
          </div>
          <div>
            <span className="font-medium">Target Revision:</span> <span className="font-bold">{revision}</span>
          </div>
        </div>
      ),
      okText: "Rollback",
      okButtonProps: { type: "primary" },
      cancelText: "Cancel",
      cancelButtonProps: { type: "default", danger: true, disabled: isLoadingRollback },
      onOk: async () => {
        try {
          setIsLoadingRollbackRevision(revision);
          setIsLoadingRollback(true);
          await request({
            service: "marketplace",
            method: "post",
            url: `/helm/rollback`,
            body: {
              org: release.org,
              name: release.name,
              revision: revision,
            },
          });
          notification.success({ message: "Success", description: "Rolled back release" });
          dispatch(addNotification({
            page: "Marketplace",
            action: "Rollback",
            success: true,
            message: `Rolled back legacy release: ${release.name} to revision ${revision}`,
          }));
          setIsLoadingRollbackRevision(null);
          setIsLoadingRollback(false);
          if (onRefresh) {
            await onRefresh();
          }
        } catch (e) {
          setIsLoadingRollbackRevision(null);
          setIsLoadingRollback(false);
          const errorMessage = getMarketplaceErrorMessage(e);
          notification.warning({ message: "Failed", description: errorMessage });
          dispatch(addNotification({
            page: "Marketplace",
            action: "Rollback",
            success: false,
            message: `Failed to rollback legacy release: ${release.name}`,
            details: Array.isArray(errorMessage) ? errorMessage.join("\n") : errorMessage,
          }));
          // Always refresh revisions even on failure
          if (onRefresh) {
            await onRefresh();
          }
        }
      },
    });
  }

  const releaseStateDeploymentTableItems: ReleaseStateDeploymentTableItem[] = state.deployments
    .toSorted((a, b) => {
      if (a.revision > b.revision) return -1;
      if (b.revision > a.revision) return 1;
      return 0;
    })
    .map((d) => {
      return {
        ...d,
        isLoadingRollback: isLoadingRollback,
        isLoadingRollbackThis: isLoadingRollbackRevision === d.revision,
      };
    });

  return (
    //
    <>
      <Table<ReleaseStateDeploymentTableItem>
        columns={[
          {
            id: "revision",
            label: "Revision",
            size: 100,
          },
          {
            id: "description",
            label: "Description",
          },
          {
            id: "status",
            label: "Status",
          },
          {
            id: "updated",
            label: "Updated",
            cell: ({ getValue }) => {
              return <DateString iso={getValue()} format={formatStrings.log} />;
            },
          },
          {
            id: "actions",
            label: "Actions",
            cell: (p) => {
              const item = p.row.original;
              const isLatest = item.revision === releaseStateDeploymentTableItems[0].revision;
              const canRollback = !isLatest && ["superseded", "deployed"].includes(item.status);

              if (!canRollback) {
                return null;
              }

              return (
                <Button
                  type="default"
                  onClick={() => handleRollback(item.revision)}
                  size={"small"}
                  disabled={item.isLoadingRollback}
                  loading={item.isLoadingRollbackThis}
                >
                  Rollback
                </Button>
              );
            },
          },
        ]}
        data={releaseStateDeploymentTableItems}
        title={"Revisions"}
        tableId="release-revisions-update1"
        hideSettings
      />
    </>
  );
};
